import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import moment from "moment-timezone";
import {
  AGENT_AGREEMENT_PREVIEW_URL,
  AGENT_AGREEMENT_SALESPERSON_FIELD,
  AGENT_AGREEMENT_BROKER_NAME,
  AGENT_AGREEMENT_SIGNATURE_PAGE,
} from "../../constants/agentAgreement.js";
import {
  buildSignedAgentAgreementPdf,
  getAgentAgreementPageCount,
  signAndUploadAgentAgreement,
  toAgentAgreementErrorMessage,
} from "../../utils/agentAgreementPdf.js";
import user_service from "../service/user_service.js";
import AgentAgreementSigningInstructions from "./AgentAgreementSigningInstructions.js";

const createInitialsDataUrl = (text) => {
  const canvas = document.createElement("canvas");
  canvas.width = 320;
  canvas.height = 140;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#111827";
  ctx.font = 'italic 72px "Times New Roman", Georgia, serif';
  ctx.textBaseline = "middle";
  ctx.fillText(text.trim(), 24, 70);
  return canvas.toDataURL("image/png");
};

const deriveInitials = (profile) => {
  if (profile?.initials) {
    return String(profile.initials).trim().toUpperCase();
  }

  return [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const AgentAgreementSignPanel = ({ contactId, profile, paperworkId, onSigned }) => {
  const padCanvasRef = useRef(null);
  const previewObjectUrlRef = useRef("");
  const [agentName, setAgentName] = useState("");
  const [initialsText, setInitialsText] = useState("");
  const [initialsDataUrl, setInitialsDataUrl] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [padMode, setPadMode] = useState("initials");
  const [pageCount, setPageCount] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(
    `${AGENT_AGREEMENT_PREVIEW_URL}#view=FitH&zoom=100`
  );
  const [isBuildingPreview, setIsBuildingPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const defaultName = [profile?.firstName, profile?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    setAgentName(defaultName);
    setInitialsText(deriveInitials(profile));
  }, [profile?.firstName, profile?.lastName, profile?.initials]);

  useEffect(() => {
    const loadPageCount = async () => {
      try {
        const count = await getAgentAgreementPageCount();
        setPageCount(count);
      } catch {
        setPageCount(AGENT_AGREEMENT_SIGNATURE_PAGE);
      }
    };

    loadPageCount();
  }, []);

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
      }
    };
  }, []);

  const jumpToSignaturePage = (url = AGENT_AGREEMENT_PREVIEW_URL) => {
    const targetPage = pageCount
      ? Math.min(AGENT_AGREEMENT_SIGNATURE_PAGE, pageCount)
      : AGENT_AGREEMENT_SIGNATURE_PAGE;
    setPreviewUrl(`${url}#page=${targetPage}&view=FitH&zoom=100`);
  };

  const resolveInitialsDataUrl = () => {
    if (initialsDataUrl) {
      return initialsDataUrl;
    }
    if (initialsText.trim()) {
      return createInitialsDataUrl(initialsText);
    }
    return "";
  };

  const refreshPreview = async (nextInitialsDataUrl, nextSignatureDataUrl) => {
    const resolvedInitials = nextInitialsDataUrl || resolveInitialsDataUrl();
    const resolvedSignature = nextSignatureDataUrl || signatureDataUrl;

    if (!agentName.trim() || !resolvedInitials || !resolvedSignature) {
      return;
    }

    setIsBuildingPreview(true);
    try {
      const previewBlob = await buildSignedAgentAgreementPdf({
        agentName: agentName.trim(),
        fieldValues: {
          [AGENT_AGREEMENT_SALESPERSON_FIELD]: agentName.trim(),
        },
        initialsText: initialsText.trim(),
        initialsDataUrl: resolvedInitials,
        signatureDataUrl: resolvedSignature,
      });

      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
      }

      const objectUrl = URL.createObjectURL(previewBlob);
      previewObjectUrlRef.current = objectUrl;
      jumpToSignaturePage(objectUrl);
      setError("");
    } catch (previewError) {
      setError(toAgentAgreementErrorMessage(previewError));
    } finally {
      setIsBuildingPreview(false);
    }
  };

  const handleSavePad = () => {
    const canvas = padCanvasRef.current;
    const isEmpty = !canvas || canvas.isEmpty();

    if (padMode === "initials") {
      if (isEmpty && !initialsText.trim()) {
        setError("Please type or draw your initials before saving.");
        return;
      }

      const nextInitials = isEmpty
        ? createInitialsDataUrl(initialsText)
        : canvas.toDataURL();
      setError("");
      setInitialsDataUrl(nextInitials);
      refreshPreview(nextInitials, signatureDataUrl);
      return;
    }

    if (isEmpty) {
      setError("Please draw your signature before saving.");
      return;
    }

    const nextSignature = canvas.toDataURL();
    setError("");
    setSignatureDataUrl(nextSignature);
    refreshPreview(
      initialsDataUrl || (initialsText.trim() ? createInitialsDataUrl(initialsText) : ""),
      nextSignature
    );
  };

  const handleClearPad = () => {
    padCanvasRef.current?.clear();
    if (padMode === "initials") {
      setInitialsDataUrl("");
    } else {
      setSignatureDataUrl("");
    }
  };

  const handleSignAgreement = async () => {
    if (!agentName.trim()) {
      setError("Please enter your name as it should appear on the agreement.");
      return;
    }

    const resolvedInitials = resolveInitialsDataUrl();
    if (!resolvedInitials) {
      setError("Please save your initials before signing the agreement.");
      return;
    }

    if (!signatureDataUrl) {
      setError("Please save your signature before signing the agreement.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await signAndUploadAgentAgreement({
        contactId,
        paperworkId,
        agentName: agentName.trim(),
        fieldValues: {
          [AGENT_AGREEMENT_SALESPERSON_FIELD]: agentName.trim(),
        },
        initialsText: initialsText.trim(),
        initialsDataUrl: resolvedInitials,
        signatureDataUrl,
      });

      let updated = { _id: contactId };
      try {
        const updatedProfile = await user_service.profileGet(contactId);
        updated = updatedProfile?.data || updatedProfile || updated;
      } catch {
        // Onboarding users are not logged in; profileGet can 401.
      }
      onSigned(updated);
    } catch (submitError) {
      setError(toAgentAgreementErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSign =
    Boolean(agentName.trim()) &&
    Boolean(signatureDataUrl) &&
    Boolean(initialsDataUrl || initialsText.trim());

  return (
    <>
      <AgentAgreementSigningInstructions />

      <div className="row align-items-stretch">
        <div className="col-md-4">
          <div className="bg-light border rounded-3 p-3">
            <div className="mb-3">
              <label className="form-label">Effective Date</label>
              <input
                className="form-control"
                type="text"
                value={moment().format("M/D/YYYY")}
                readOnly
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Broker</label>
              <input
                className="form-control"
                type="text"
                value={AGENT_AGREEMENT_BROKER_NAME}
                readOnly
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Salesperson Name</label>
              <input
                className="form-control"
                type="text"
                autoComplete="name"
                name={AGENT_AGREEMENT_SALESPERSON_FIELD}
                value={agentName}
                onChange={(event) => setAgentName(event.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Initials</label>
              <input
                className="form-control"
                type="text"
                maxLength={8}
                autoComplete="off"
                value={initialsText}
                onChange={(event) => setInitialsText(event.target.value.toUpperCase())}
                placeholder="e.g. SK"
              />
            </div>

            <div className="mb-3">
              <div className="btn-group w-100 mb-2" role="group" aria-label="Pad type">
                <button
                  type="button"
                  className={`btn btn-sm ${padMode === "initials" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => {
                    padCanvasRef.current?.clear();
                    setPadMode("initials");
                  }}
                >
                  Initials
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${padMode === "signature" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => {
                    padCanvasRef.current?.clear();
                    setPadMode("signature");
                  }}
                >
                  Signature
                </button>
              </div>

              <label className="form-label">
                {padMode === "initials" ? "Draw initials" : "Draw signature"}
              </label>
              <div className="canvas_signature w-100">
                <SignatureCanvas
                  ref={padCanvasRef}
                  canvasProps={{
                    className: "signature-canvas w-100",
                    style: { height: 150, border: "1px solid #ccc" },
                  }}
                />
                <div className="float-left w-100 mt-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleClearPad}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm ms-2"
                    onClick={handleSavePad}
                  >
                    {padMode === "initials" ? "Save Initials" : "Save Signature"}
                  </button>
                </div>
                <p className="small mt-2 mb-0">
                  {initialsDataUrl ? (
                    <span className="text-success d-block">Initials saved.</span>
                  ) : initialsText.trim() ? (
                    <span className="text-muted d-block">
                      Typed initials will be used if you do not draw them.
                    </span>
                  ) : null}
                  {signatureDataUrl ? (
                    <span className="text-success d-block">Signature saved.</span>
                  ) : (
                    <span className="text-muted d-block">
                      Draw and save a signature before signing.
                    </span>
                  )}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-outline-primary btn-sm mb-3"
              onClick={() =>
                jumpToSignaturePage(previewObjectUrlRef.current || AGENT_AGREEMENT_PREVIEW_URL)
              }
            >
              Jump to signature page
            </button>

            <button
              type="button"
              className="btn btn-primary w-100"
              disabled={isSubmitting || isBuildingPreview || !canSign}
              onClick={handleSignAgreement}
            >
              {isSubmitting
                ? "Signing..."
                : isBuildingPreview
                  ? "Updating preview..."
                  : "Sign Agreement"}
            </button>

            {error ? <p className="text-danger mt-3 mb-0">{error}</p> : null}
          </div>
        </div>

        <div className="col-md-8">
          <div
            className="agent-agreement-iframe-wrap"
            style={{
              height: "calc(100vh - 240px)",
              minHeight: "1100px",
            }}
          >
            <iframe
              key={previewUrl}
              title="Broker Agent Agreement"
              src={previewUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AgentAgreementSignPanel;
